import { t, localizedName } from "../utils/i18n.ts";
import { COLORS } from "../core/constants.ts";
import type { Card } from "../entities/types.ts";
import { MAX_SLOTS } from "../systems/DraftSystem.ts";
import { ProBar } from "./ProBar.ts";
import { UI_FONT_STACK, UI_SPACE, UI_EASE, UI_DURATION, roleFontSize } from "./uiTokens.ts";
import { RARITY_SLOT_COLORS } from "../core/palette.ts";
import { InfoTooltip } from "./InfoTooltip.ts";
import { makeKeyboardActivatable } from "./a11y.ts";
import { getStageFit } from "../utils/stage.ts";

/**
 * Skill Deck Bar — shows the current loadout.
 * Layer 4 (HTML/CSS). Updates reactively via EventBus.
 * Supports dynamic slot count (bit_packer module adds +1).
 *
 * D-333 — relocated to the LEFT rail (mounted INSIDE `#status-row`, below the
 * left HUD), right-aligned so it clears the iPhone Dynamic Island. `// PAYLOAD`
 * (`PayloadRackBar`) stays in the right rail — only this block moved. The
 * standalone "SKILLS [S]" open row is gone; the "// LOADOUT" header itself is
 * now the tap target (see `makeKeyboardActivatable`) — the single biggest px
 * saving in the block's vertical budget (see docs/decisions.md D-333).
 */
export class SkillDeckBar {
  private container: HTMLDivElement;
  private slotsContainer: HTMLDivElement;
  private slots: HTMLDivElement[] = [];
  /** D-336 — each slot is now a borderless label-left/value-right row (the
   *  SAME logic the status panel above uses): the name on the left, a small
   *  element/rarity marker (a lit dot, + the "[NW]" countdown text when a
   *  card is temporary) on the right. Kept as separate refs (rather than
   *  rebuilding `slot.textContent` wholesale) so the marker can carry its
   *  own colour/content independent of the name. */
  private slotNames: HTMLSpanElement[] = [];
  private slotMarkers: HTMLSpanElement[] = [];
  /** BL-56 — one depleting underline Pro-Bar per slot for duration cards (the
   *  `[NW]` remaining tag → a lit bar that drains as the waves tick down). */
  private slotBars: (ProBar | null)[] = [];
  private header: HTMLDivElement;
  private onOpen: (() => void) | null = null;
  private currentMaxSlots: number = MAX_SLOTS;
  private currentCards: Card[] = [];
  private readonly tooltip = new InfoTooltip();
  private readonly onResize = (): void => this.updateIslandClearance();

  constructor(parent: HTMLElement, onOpen: () => void) {
    this.onOpen = onOpen;
    this.container = document.createElement("div");
    this.container.id = "skill-deck-bar";
    this.injectStyles();

    // Section header — D-333: now the ONLY affordance that opens the skill
    // overview (the old standalone "SKILLS [S]" row is retired). Keyboard-
    // activatable via the shared a11y helper (Enter/Space), and the "S"
    // shortcut (inputHandlers.ts KeyS) keeps working unchanged — this is an
    // additional pointer/keyboard path onto the SAME `onOpen`, not a
    // replacement for it.
    this.header = document.createElement("div");
    this.header.className = "deck-header";
    this.header.setAttribute("aria-label", t("skill.overview.open"));
    const headerLabel = document.createElement("span");
    headerLabel.className = "deck-header-label";
    headerLabel.textContent = t("skilldeck.header");
    const headerHint = document.createElement("span");
    headerHint.className = "deck-header-hint";
    headerHint.textContent = t("skilldeck.headerHint");
    headerHint.setAttribute("aria-hidden", "true");
    this.header.appendChild(headerLabel);
    this.header.appendChild(headerHint);
    makeKeyboardActivatable(this.header, () => this.onOpen?.());
    this.container.appendChild(this.header);

    this.slotsContainer = document.createElement("div");
    this.slotsContainer.className = "deck-slots-container";
    this.container.appendChild(this.slotsContainer);

    this.rebuildSlots(MAX_SLOTS);

    parent.appendChild(this.container);
    this.updateIslandClearance();
    window.addEventListener("resize", this.onResize);
    window.addEventListener("orientationchange", this.onResize);
  }

  /**
   * D-333 — Dynamic Island clearance, COMPUTED not hard-coded. The block is
   * right-aligned, so its left edge is what must clear the Island (the rail's
   * outer/leading edge in landscape). `window.__stageFit.insets` are REAL
   * (post-scale) px; dividing by the live stage scale (`getStageFit().scale`,
   * `clientLenToStage`'s own formula) converts that into the DESIGN-space px
   * this block's `max-width` needs — so the clearance tracks every device's
   * actual scale instead of assuming one (the HUD.ts:1217/:1199,
   * EnemyReveal.ts:135-136, index.html:192 class of bug this decision's D-333
   * entry files as NOT fixed here — those are pre-existing hard-coded design-px
   * insets outside this change's surface).
   */
  private updateIslandClearance(): void {
    const fit = getStageFit();
    const clearance = fit.scale > 0 ? fit.insets.left / fit.scale : 0;
    this.container.style.setProperty("--loadout-island-clear", `${clearance}px`);
  }

  /** Release the resize/orientation listeners (tests / a future teardown path). */
  destroy(): void {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("orientationchange", this.onResize);
  }

  update(cards: Card[], maxSlots?: number, currentWave?: number): void {
    this.currentCards = cards;
    const effectiveMax = maxSlots ?? this.currentMaxSlots;
    if (effectiveMax !== this.currentMaxSlots) {
      this.rebuildSlots(effectiveMax);
    }

    for (let i = 0; i < this.currentMaxSlots; i++) {
      const slot = this.slots[i]!;
      const nameEl = this.slotNames[i]!;
      const markerEl = this.slotMarkers[i]!;
      const card = cards[i];
      if (card) {
        const rarityColor =
          RARITY_SLOT_COLORS[card.rarity] ?? COLORS.SENTINEL_GREEN;
        let remaining = 0;
        let markerText = "";
        if (card.duration) {
          remaining =
            currentWave != null && card._selectedAtWave != null
              ? card._selectedAtWave + card.duration - currentWave
              : card.duration;
          markerText = `[${remaining}W]`;
        }
        nameEl.textContent = localizedName("card", card.id, card.name);
        markerEl.textContent = markerText;
        slot.style.color = rarityColor;
        slot.classList.add("deck-slot--filled");
        slot.classList.remove("deck-slot--empty"); // BL-236
        slot.classList.toggle("deck-slot--temp", !!card.duration);
        slot.classList.toggle(
          "deck-slot--legendary",
          card.rarity === "legendary",
        );
        if (card.duration) {
          this.ensureSlotBar(i, slot, rarityColor, card.duration, remaining);
        } else {
          this.clearSlotBar(i);
        }
      } else {
        nameEl.textContent = t("skill.deck.empty");
        markerEl.textContent = "";
        slot.style.color = COLORS.TEXT_SECONDARY;
        // BL-236 — an empty slot reads as a recessed SOCKET waiting for a
        // card (a dim, italic label + a dim marker dot), not a flat "EMPTY"
        // line — but D-336 dropped the dashed BOX that used to carry that
        // read (the left rail is borderless now); the dim colour + italic +
        // the marker dot alone still say "empty".
        slot.classList.add("deck-slot--empty");
        slot.classList.remove(
          "deck-slot--filled",
          "deck-slot--temp",
          "deck-slot--legendary",
        );
        this.clearSlotBar(i);
      }
    }
  }

  /** Lazily create / update a slot's depleting underline Pro-Bar. The slot's
   *  `textContent` reset detaches the bar el, so we re-append it each update. */
  private ensureSlotBar(
    index: number,
    slot: HTMLDivElement,
    color: string,
    duration: number,
    remaining: number,
  ): void {
    let bar = this.slotBars[index] ?? null;
    if (!bar) {
      bar = new ProBar({
        color,
        max: duration,
        value: remaining,
        variant: "underline",
        valueText: "", // the slot's marker text already shows "[NW]" — bar is visual
        extraClasses: ["deck-slot-bar"],
      });
      this.slotBars[index] = bar;
    }
    if (bar.el.parentNode !== slot) slot.appendChild(bar.el);
    bar.setMax(duration);
    bar.setColor(color);
    bar.setValue(Math.max(0, remaining));
    bar.render();
  }

  private clearSlotBar(index: number): void {
    const bar = this.slotBars[index];
    if (bar) {
      bar.destroy();
      this.slotBars[index] = null;
    }
  }

  private rebuildSlots(count: number): void {
    for (const bar of this.slotBars) bar?.destroy();
    this.slotBars = [];
    this.slotsContainer.innerHTML = "";
    this.slots = [];
    this.slotNames = [];
    this.slotMarkers = [];
    this.currentMaxSlots = count;
    for (let i = 0; i < count; i++) {
      const slot = document.createElement("div");
      slot.className = "deck-slot";
      // D-336 — the borderless label-left/value-right row: a name span (left)
      // + a small element/rarity marker (right) — the SAME logic the status
      // rows above use, replacing the old single boxed text node.
      const nameEl = document.createElement("span");
      nameEl.className = "deck-slot-name";
      nameEl.textContent = t("skill.deck.empty");
      const markerEl = document.createElement("span");
      markerEl.className = "deck-slot-marker";
      // no aria-hidden here: the marker carries the "[NW]" remaining-wave
      // countdown text for temporary cards, which must stay in the slot's
      // accessible name; the rarity dot is a CSS ::before pseudo-element and
      // so contributes no text of its own.
      slot.append(nameEl, markerEl);
      slot.addEventListener("mouseenter", () => this.showSlotTooltip(slot, i));
      slot.addEventListener("mouseleave", () => this.hideSlotTooltip());
      this.slotsContainer.appendChild(slot);
      this.slots.push(slot);
      this.slotNames.push(nameEl);
      this.slotMarkers.push(markerEl);
    }
  }

  show(): void {
    this.container.style.display = "flex";
  }

  hide(): void {
    this.container.style.display = "none";
  }

  private showSlotTooltip(slot: HTMLDivElement, index: number): void {
    this.hideSlotTooltip();
    const card = this.currentCards[index];
    if (!card) return;
    const rarityColor =
      RARITY_SLOT_COLORS[card.rarity] ?? COLORS.SENTINEL_GREEN;
    const cardName = localizedName("card", card.id, card.name);
    const cardDesc = localizedName("card", `${card.id}.desc`, card.description ?? "");
    const cardHeadline = card.headline
      ? localizedName("card", `${card.id}.headline`, card.headline)
      : card.headline;
    // CR-1: the shared card-style tooltip — name → IMPACT (headline) → body →
    // meta (rarity · permanent/N waves).
    this.tooltip.show(slot, {
      name: cardName,
      accent: rarityColor,
      tag: card.rarity,
      impact: cardHeadline,
      body: cardDesc ? [cardDesc] : [],
      meta: `[${card.rarity}]${card.duration ? ` ${card.duration} waves` : " permanent"}`,
    });
  }

  private hideSlotTooltip(): void {
    this.tooltip.hide();
  }

  private injectStyles(): void {
    if (document.getElementById("skill-deck-styles")) return;
    const style = document.createElement("style");
    style.id = "skill-deck-styles";
    style.textContent = `
      #skill-deck-bar {
        display: flex;
        flex-direction: column;
        gap: 0;
        padding: 0;
        font-family: ${UI_FONT_STACK};
        user-select: none;
        /* D-333 — mounted INSIDE #status-row (left rail), below the left HUD.
           RIGHT-ALIGNED (margin-left: auto on a bounded width) so the block
           sits away from the rail's outer/leading edge — the iPhone Dynamic
           Island side in landscape. The bound is the SMALLER of a comfortable
           reading width and (100% − the computed Island clearance), so on a
           narrow rail the block never gets pushed UNDER the clearance zone
           either. --loadout-island-clear is set in JS (updateIslandClearance,
           SkillDeckBar.ts) from the REAL safe-area inset divided by the live
           stage scale — never a hard-coded design-px inset (see D-333). */
        /* BL-291 — was right-aligned at a bounded 220px, which put its leading
           edge at 210 design px while the status block above started at 143.1
           and the dock below at 135.1. It also meant the block ignored the
           width this rail gained: 220 of ~287 available. It now fills the
           rail's shared content column like its neighbours, so the three read
           as one instrument and the loadout finally uses the rail. */
        width: auto;
        margin: 6px var(--rail-pad-r) 0 var(--rail-pad-l);
        box-sizing: border-box;
        /* BL-291 — and it joins the rail's FRAME family. #hud-left above it
           draws a rounded slab on a lit ground with a hairline sentinel ring;
           this block drew nothing at all, so the rail read as panel / loose
           list / panel. Same radius, same ring, a fractionally quieter ground
           so the status block still leads. */
        border-radius: var(--rail-radius, 8px);
        /* A darkening veil rather than an opaque fill: no new colour identity
           enters the codebase (the inline-hex ratchet is right to refuse one),
           and it composites over whatever ground the rail already has instead
           of second-guessing it. */
        background: linear-gradient(
          180deg,
          rgba(0, 0, 0, 0.28),
          rgba(0, 0, 0, 0.42) 60%,
          rgba(0, 0, 0, 0.48)
        );
        box-shadow:
          0 0 0 1px color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 18%, transparent),
          0 14px 40px -24px rgba(0, 0, 0, 0.85);
        overflow: hidden;
      }
      /* BL-62 — "// LOADOUT" harmonised to the shared section-header green
         (#6f8a72), matching the signed mockup's "//" rhythm across the rail.
         D-333 — the header is now ALSO the "open skill overview" tap target
         (the standalone "SKILLS [S]" row is retired — the biggest single px
         saving in the relocated block's vertical budget); see
         makeKeyboardActivatable for the click/Enter/Space wiring. */
      .deck-header {
        padding: ${UI_SPACE.sm}px ${UI_SPACE.lg}px ${UI_SPACE.xs}px;
        font-size: ${roleFontSize("micro")};
        color: #6f8a72;
        letter-spacing: 2px;
        text-transform: uppercase;
        border-bottom: 1px solid color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 22%, transparent);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${UI_SPACE.xs}px;
        cursor: pointer;
        transition: background ${UI_DURATION.hover}ms ${UI_EASE.settle},
                    color ${UI_DURATION.hover}ms ${UI_EASE.settle};
      }
      .deck-header:hover, .deck-header:focus-visible {
        background: color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 9%, transparent);
        color: #7fff7f;
      }
      .deck-header-hint {
        flex: none;
        opacity: 0.85;
      }
      @media (prefers-reduced-motion: reduce) {
        .deck-header { transition: none; }
      }
      /* D-336 (owner rejection — "die skills so anders...") — a loadout slot
         used to be a bordered, filled box (a left rarity edge + an inset
         gradient + a bottom rule) while every status row above it carried NO
         box at all — two visual languages stacked in one rail. Dropped: the
         slot is now a BORDERLESS row in the SAME rhythm as CREDITS/
         INTEGRITY/WAVE ('.hud-row') — label (the card name) LEFT, a small
         element/rarity marker RIGHT ('.deck-slot-marker', below) — one
         alignment logic, top to bottom. D-333's vertical padding tightening
         (hair, 2px) is unchanged (still real budget headroom against the
         D-333 vertical-budget guard). */
      .deck-slot {
        position: relative;
        width: 100%;
        padding: ${UI_SPACE.hair}px ${UI_SPACE.md}px;
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: ${UI_SPACE.xs}px;
        color: ${COLORS.TEXT_SECONDARY};
        font-size: ${roleFontSize("micro")};
        /* BL-186 tabular-nums so the [NW] countdown tag doesn't reflow the slot. */
        font-variant-numeric: tabular-nums;
        letter-spacing: 0.5px;
        transition: color ${UI_DURATION.hover}ms ${UI_EASE.settle};
      }
      .deck-slot:hover {
        color: color-mix(in srgb, currentColor 30%, ${COLORS.TEXT_PRIMARY});
      }
      .deck-slot-name {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      /* The right-hand marker: a lit dot in the slot's own colour (the
         element/rarity read the boxed left-edge used to carry) + the "[NW]"
         countdown text when the card is temporary — the SAME label-left/
         value-right logic the status rows above use. */
      .deck-slot-marker {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: ${UI_SPACE.xxs}px;
        opacity: 0.85;
      }
      .deck-slot-marker::before {
        content: "";
        width: 6px;
        height: 6px;
        flex: none;
        border-radius: 50%;
        background: currentColor;
        box-shadow: 0 0 4px currentColor;
      }
      /* BL-56 — the duration underline bar, pinned to the slot's bottom edge. */
      .deck-slot-bar {
        position: absolute;
        left: 0; right: 0; bottom: 0; height: 3px;
        pointer-events: none;
      }
      .deck-slot--filled {
        color: ${COLORS.TEXT_PRIMARY};
      }
      .deck-slot--filled .deck-slot-name { color: currentColor; }
      /* BL-236 — an EMPTY loadout slot still reads as waiting for a card: a
         dim, italic label + a dim marker dot (no boxed/dashed channel any
         more — D-336 dropped the box language rail-wide). */
      .deck-slot--empty {
        opacity: 0.7;
        font-style: italic;
        letter-spacing: 1.5px;
      }
      .deck-slot--empty .deck-slot-marker { color: ${COLORS.SENTINEL_GREEN_DIM}; }
      /* BL-189 calm-pass (owner steer "calm the ambient, keep the beats"): the
         always-on tempPulse / goldSlotGlow infinite loops are STILLED. The
         "temporary" read is carried by the italic + the depleting under-bar
         (.deck-slot-bar), and the "legendary" read by the gold text-shadow on
         the name — both static, both still premium, neither competing with
         the rest of the rail. One ambient lead at a time (CommentaryTV keeps it). */
      .deck-slot--temp {
        font-style: italic;
      }
      .deck-slot--legendary .deck-slot-name {
        text-shadow: 0 0 6px rgba(255,215,0,0.6);
      }
      /* BL-195 motion-a11y: under prefers-reduced-motion every slot animation +
         transition is hard-killed (the slots stay fully lit + legible). Closes
         the net's reduced-motion gap on this surface. */
      @media (prefers-reduced-motion: reduce) {
        .deck-slot,
        .deck-slot--temp,
        .deck-slot--legendary { animation: none; transition: none; }
      }
    `;
    document.head.appendChild(style);
  }
}
